import React, { useState, useEffect, useCallback } from 'react';

interface ArtworkModalProps {
    isOpen: boolean;
    onClose: () => void;
    images: string[];
    title: string;
    initialIndex?: number;
}

const ArtworkModal: React.FC<ArtworkModalProps> = ({ isOpen, onClose, images, title, initialIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
            setZoom(1);
            setPosition({ x: 0, y: 0 });
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isOpen, initialIndex]);

    const handleNext = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setCurrentIndex((prev) => (prev + 1) % images.length);
    }, [images.length]);

    const handlePrev = useCallback((e?: React.MouseEvent) => {
        e?.stopPropagation();
        setZoom(1);
        setPosition({ x: 0, y: 0 });
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    }, [images.length]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'ArrowLeft') handlePrev();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, handleNext, handlePrev]);

    const toggleZoom = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (zoom > 1) {
            setZoom(1);
            setPosition({ x: 0, y: 0 });
        } else {
            setZoom(2.5);
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (zoom <= 1) return;
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || zoom <= 1) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => setIsDragging(false);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center animate-fade-in"
            onClick={onClose}
        >
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 p-6 flex justify-between items-center z-[110] bg-transparent pointer-events-none">
                <div className="text-white pointer-events-none drop-shadow-md">
                    <h2 className="text-xl md:text-2xl font-serif font-bold tracking-wide">{title}</h2>
                    <p className="text-[10px] uppercase tracking-widest text-gray-400">
                        Foto {currentIndex + 1} van {images.length}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="w-14 h-14 flex items-center justify-center text-white/70 hover:text-white bg-black/40 hover:bg-black/80 rounded-full backdrop-blur-sm pointer-events-auto transition-all"
                    aria-label="Sluiten"
                >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Main Stage */}
            <div
                className="relative w-full h-full flex items-center justify-center overflow-hidden cursor-zoom-in"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onClick={toggleZoom}
            >
                <img
                    src={images[currentIndex]}
                    alt={`${title} - foto ${currentIndex + 1}`}
                    className={`w-full h-full object-contain transition-transform duration-300 ${isDragging ? '' : 'ease-out'}`}
                    style={{
                        transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                        cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
                    }}
                    draggable={false}
                />

                {/* Navigation Arrows */}
                {images.length > 1 && (
                    <>
                        <button
                            onClick={handlePrev}
                            className="absolute left-4 md:left-8 w-16 h-16 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-black/80 hover:bg-black border-2 md:border-4 border-white/40 hover:border-white text-white/80 hover:text-white backdrop-blur-md transition-all shadow-2xl z-[120] group"
                        >
                            <svg className="w-8 h-8 md:w-12 md:h-12 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            onClick={handleNext}
                            className="absolute right-4 md:right-8 w-16 h-16 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-black/80 hover:bg-black border-2 md:border-4 border-white/40 hover:border-white text-white/80 hover:text-white backdrop-blur-md transition-all shadow-2xl z-[120] group"
                        >
                            <svg className="w-8 h-8 md:w-12 md:h-12 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </>
                )}
            </div>



            {/* Zoom Hint */}
            <div className="absolute top-8 left-1/2 -translate-x-1/2 text-white/50 text-[11px] font-semibold uppercase tracking-widest pointer-events-none drop-shadow-md pb-2 bg-black/40 px-6 py-2 rounded-full backdrop-blur-sm z-[110]">
                {zoom > 1 ? 'Sleep om te bewegen • Klik om uit te zoomen' : 'Klik om in te zoomen • Pijltoetsen voor slideshow'}
            </div>
        </div>
    );
};

export default ArtworkModal;
